import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Toolbar,
  useMediaQuery,
  useTheme,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  Stack,
  Container
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Flag,
  Search,
  Star,
  Chat,
  Paid
} from "@mui/icons-material";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import routes from "../../../Config/routes";
import collaborateImage from "../../../Components/globalAssets/blackwhite.png";

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };

const stats = [
  { value: "1000+", label: "Campaigns Launched" },
  { value: "1200+", label: "Creators Onboarded" },
  { value: "98%", label: "Collaboration Success" },
  { value: "48h", label: "Avg Response Time" }
];

const modules = [
  {
    icon: <Flag fontSize="large" />,
    title: "Blitz Campaigns",
    description:
      "Create and manage campaigns end-to-end: budgets, timelines, deliverables, approvals."
  },
  {
    icon: <Search fontSize="large" />,
    title: "Marketplace",
    description:
      "Browse, filter, and shortlist creators; view rich profiles and broadcast open briefs."
  },
  {
    icon: <Star fontSize="large" />,
    title: "Blitz Partnerships",
    description:
      "Promote top creators to Partner or Associate with one-click email-alias management."
  },
  {
    icon: <Chat fontSize="large" />,
    title: "Blitz Chats",
    description:
      "In-app negotiation and direct deals with deliverable checklists and approvals."
  },
  {
    icon: <Paid fontSize="large" />,
    title: "BlitzPay",
    description:
      "Trigger instant payouts, manage advances, and reconcile all transactions seamlessly."
  }
];

export default function Collaborate() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const accentColor = theme.palette.mode === "light" ? "#0d47a1" : "#90caf9";
  const statsColor = theme.palette.mode === "light" ? "#1e88e5" : "#4fc3f7";
  const refs = useRef({});

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      refs.current[id]?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.hash]);

  const handleNavigate = () => navigate(routes.onboard);

  const ModuleCard = ({ icon, title, description }) => (
    <Paper
      component={motion.div}
      variants={fadeIn}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 3,
        borderLeft: `4px solid ${accentColor}`,
        boxShadow: theme.shadows[2],
        transition: "transform 0.3s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: theme.shadows[6]
        }
      }}
    >
      <Box sx={{ color: accentColor, mb: 2 }}>{icon}</Box>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {description}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default }}>
      <HomeHeader />
      <Toolbar />

      {/* Hero Section */}
      <Box
        component="section"
        ref={el => (refs.current['hero'] = el)}
        sx={{
          py: 10,
          background: `linear-gradient(135deg, ${accentColor} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "#fff"
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center" spacing={6}
            >
            <Grid item xs={12} md={6}>
              <Typography
                component={motion.div}
                variants={fadeIn}
                variant={isMobile ? "h4" : "h2"}
                gutterBottom
                sx={{ fontWeight: 800 }}
              >
                Empower Your Creator Collaborations
              </Typography>
              <Typography
                component={motion.div}
                variants={fadeIn}
                variant="h5"
                sx={{ mb: 4, fontWeight: 300 }}
              >
                Discover talent, manage campaigns, and streamline workflows with Blitz Collaborate
              </Typography>
              <Button
                component={motion.div}
                variants={fadeIn}
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleNavigate}
                sx={{
                  fontSize: "1.1rem",
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                  boxShadow: theme.shadows[4]
                }}
              >
                Get Started
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                variants={fadeIn}
                sx={{
                  background: `linear-gradient(145deg, ${theme.palette.background.paper}, #e0e0e0)`,
                  borderRadius: 4,
                  p: 2,
                  boxShadow: theme.shadows[10]
                }}
              >
                <img
                  src={collaborateImage}
                  alt="Blitz Collaborate Dashboard"
                  style={{
                    width: '100%',
                    borderRadius: 12,
                    border: `1px solid ${theme.palette.divider}`
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, backgroundColor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box textAlign="center">
                  <Typography
                    variant="h2"
                    component={motion.div}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    sx={{ fontWeight: 800, color: statsColor, mb: 1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body1">{stat.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Modules Section */}
      <Box
        id="modules"
        ref={el => (refs.current['modules'] = el)}
        sx={{ py: 8 }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 6 }}
          >
            Key Collaborate Modules
          </Typography>

          <Grid container spacing={4}>
            {modules.map((mod, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <ModuleCard
                  icon={mod.icon}
                  title={mod.title}
                  description={mod.description}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      <Box
        id="how-it-works"
        ref={el => (refs.current['how-it-works'] = el)}
        sx={{
          py: 8,
          backgroundColor: theme.palette.dark
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ fontWeight: 700, mb: 6 }}
          >
            How Collaborate Works
          </Typography>

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <List sx={{ width: '100%' }}>
                {[
                  { icon: <Flag sx={{ color: accentColor }} />, title: "Create Campaigns & Tasks", text: "Set objectives, budgets, deliverables, and track status in one place." },
                  { icon: <Search sx={{ color: accentColor }} />, title: "Source & Shortlist", text: "Discover creators via filters, demographics, and past work." },
                  { icon: <Star sx={{ color: accentColor }} />, title: "Manage Partnerships", text: "Toggle Associate/Partner status with email-alias forwarding." },
                  { icon: <Chat sx={{ color: accentColor }} />, title: "Chat & Approve", text: "Negotiate scope, revisions, and finalize deliverables in-app." },
                  { icon: <Paid sx={{ color: accentColor }} />, title: "Instant Payouts", text: "Trigger BlitzPay payouts immediately upon approval." }
                ].map((item, i) => (
                  <ListItem key={i} sx={{ alignItems: 'flex-start', mb: 2, p: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 1 }}>
                      {item.icon}
                    </ListItemIcon>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {item.text}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: theme.shadows[4]
                }}
              >
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between', mb: 3,
                  p: 2, backgroundColor: theme.palette.grey, borderRadius: 2
                }}>
                  <Typography variant="body1"><strong>Task</strong></Typography>
                  <Typography variant="body1"><strong>Standard</strong></Typography>
                  <Typography variant="body1"><strong>Collaborate</strong></Typography>
                </Box>

                {[
                  { feature: "Setup Time", standard: "Days", collaborate: "Minutes" },
                  { feature: "Visibility", standard: "Limited", collaborate: "Full Pipeline" },
                  { feature: "Communication", standard: "Email/Calls", collaborate: "In-App Chat" },
                  { feature: "Payouts", standard: "Net Terms", collaborate: "Instant via BlitzPay" }
                ].map((row, idx) => (
                  <Box key={idx} sx={{
                    display: 'flex', justifyContent: 'space-between', p: 2,
                    backgroundColor: idx % 2 === 0 ? theme.palette.action.hover : 'transparent',
                    borderRadius: 1
                  }}>
                    <Typography variant="body1"><strong>{row.feature}</strong></Typography>
                    <Typography variant="body1">{row.standard}</Typography>
                    <Typography variant="body1" sx={{ color: accentColor, fontWeight: 600 }}>
                      {row.collaborate}
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 8, background: `linear-gradient(135deg, ${accentColor} 0%, ${theme.palette.primary.dark} 100%)` }}>
        <Container maxWidth="sm">
          <Stack alignItems="center" textAlign="center" spacing={3}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: "#fff" }}>
              Ready to Supercharge Your Collaborations?
            </Typography>
            <Typography variant="h5" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 300 }}>
              Join thousands of brands and creators on Blitz Collaborate
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={handleNavigate}
              sx={{
                fontSize: "1.1rem",
                px: 6,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: theme.shadows[10],
                mt: 2
              }}
            >
              Get Started Free
            </Button>
          </Stack>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
}
